import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useConsent from '../../hooks/useConsent';
import { clearLanguagePreference } from '../../i18n';
// import GAScript from './GAScript';
import GTMScript from './GTMScript';
import './cookieConsent.scss';

const CustomCookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isPersonalizing, setIsPersonalizing] = useState(false);
  const [consent, saveConsent] = useConsent();
  const [preferences, setPreferences] = useState(() => {
    // Inicializar preferencias desde localStorage solo una vez
    const savedPreferences = localStorage.getItem('cookiePreferences');
    return savedPreferences ? JSON.parse(savedPreferences) : {
      analytics: false,
      functional: false,
    };
  });
  const [isServiceWorkerRegistered, setIsServiceWorkerRegistered] = useState(false);

  // Memoizar la función notifyServiceWorker
  const notifyServiceWorker = useCallback((newPreferences) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'UPDATE_CONSENT',
          preferences: newPreferences,
        });
      } catch (error) {
        console.warn('No se pudo notificar al Service Worker:', error);
      }
    }
  }, []);

  // Memoizar las funciones de manejo de consentimiento
  const handleAcceptAll = useCallback(() => {
    const newPreferences = {
      analytics: true,
      functional: true,
      marketing: true,
      tagManager: true,
      necessary: true,
    };
    setPreferences(newPreferences);
    localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences));
    localStorage.setItem('cookieConsent', 'true');
    setIsVisible(false);
    notifyServiceWorker(newPreferences);
  }, [notifyServiceWorker]);

  const handleDeclineAll = useCallback(() => {
    const newPreferences = {
      analytics: false,
      functional: false,
      marketing: false,
      tagManager: false,
      necessary: true,
    };
    setPreferences(newPreferences);
    localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences));
    localStorage.setItem('cookieConsent', 'true');
    setIsVisible(false);
    deleteCookies();
    notifyServiceWorker(newPreferences);
  }, [notifyServiceWorker]);

  const handleSavePreferences = useCallback(() => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    localStorage.setItem('cookieConsent', 'true');
    setIsVisible(false);
    notifyServiceWorker(preferences);
  }, [preferences, notifyServiceWorker]);

  // Efecto para inicializar el Service Worker
  useEffect(() => {
    // Verificar si el banner debe mostrarse (solo una vez)
    const hasConsented = localStorage.getItem('cookieConsent');
    if (!hasConsented) {
      setIsVisible(true);
    }

    // Registrar Service Worker solo si no está registrado
    if ('serviceWorker' in navigator && !isServiceWorkerRegistered) {
      const registerServiceWorker = async () => {
        try {
          const existingRegistration = await navigator.serviceWorker.getRegistration('/cookieWorker.js');

          if (existingRegistration) {
            await existingRegistration.update();
            setIsServiceWorkerRegistered(true);
          } else {
            const registration = await navigator.serviceWorker.register('/cookieWorker.js', {
              scope: '/',
              updateViaCache: 'none'
            });
            setIsServiceWorkerRegistered(true);
          }
        } catch (error) {
          console.error('Error al registrar/actualizar el Service Worker:', error);
        }
      };

      registerServiceWorker();
    }
  }, []); // Solo se ejecuta una vez al montar el componente

  // Efecto separado para el manejo de mensajes del Service Worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const messageHandler = (event) => {
      if (event.data && event.data.type === 'GET_CONSENT' && event.ports && event.ports[0]) {
        event.ports[0].postMessage({ preferences });
      }
    };

    navigator.serviceWorker.addEventListener('message', messageHandler);
    return () => navigator.serviceWorker.removeEventListener('message', messageHandler);
  }, [preferences]);

  const deleteCookies = (name) => {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const cookieName = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      // Intenta eliminar la cookie con diferentes combinaciones de path y domain
      const domains = [
        '', // dominio actual
        window.location.hostname, // dominio explícito
        `.${window.location.hostname}`, // subdominio
        window.location.hostname.split('.').slice(1).join('.'), // dominio padre
        `.${window.location.hostname.split('.').slice(1).join('.')}` // dominio padre con punto
      ];

      const paths = ['/', '/admin', '', window.location.pathname];

      if (cookieName.startsWith(name)) {
        domains.forEach(domain => {
          paths.forEach(path => {
            document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domain ? `;domain=${domain}` : ''}`;
          });
        });
      }
    }
  };

  const handlePersonalize = () => {
    setIsPersonalizing(true);
  };

  const handleWithdrawConsent = () => {
    setIsVisible(true);
    setIsPersonalizing(true);
  };

  return (
    <>
      {consent?.tagManager && <GTMScript />}

      {isVisible && (
        <div
          className="cookie_banner"
          role="dialog"
          aria-labelledby="cookie-consent-title"
        >
          <div className="container">
            <h3 id="cookie-consent-title">{t('cookieConsentTitle')}</h3>
            <p>{t('cookieConsentDescription')}</p>
            <a href="/cookies" target="_blank" rel="noopener noreferrer">
              {t('cookieConsentLearnMore')}
            </a>
            {isPersonalizing ? (
              <div className="cookie-notice">
                <div className="cookie_list">
                  <div className="cookie_item">
                    <input
                      type="checkbox"
                      id="necessary-cookies"
                      checked={preferences.necessary}
                      disabled
                    />
                    <label htmlFor="necessary-cookies">
                      {t('cookieTypeEssential')}
                    </label>
                    <p>{t('cookieTypeEssentialDescription')}</p>
                  </div>
                  <div className="cookie_item">
                    <input
                      type="checkbox"
                      id="functional-cookies"
                      checked={preferences.functional}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          functional: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="functional-cookies">
                      {t('cookieTypeFunctional')}
                    </label>
                    <p>{t('cookieTypeFunctionalDescription')}</p>
                  </div>
                  <div className="cookie_item">
                    <input
                      type="checkbox"
                      id="analytics-cookies"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          analytics: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="analytics-cookies">
                      {t('cookieTypeAnalytics')}
                    </label>
                    <p>{t('cookieTypeAnalyticsDescription')}</p>
                  </div>
                  <div className="cookie_item">
                    <input
                      type="checkbox"
                      id="marketing-cookies"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          marketing: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="marketing-cookies">
                      {t('cookieTypeMarketing')}
                    </label>
                    <p>{t('cookieTypeMarketingDescription')}</p>
                  </div>
                  <div className="cookie_item">
                    <input
                      type="checkbox"
                      id="tagmanager-cookies"
                      checked={preferences.tagManager}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          tagManager: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="tagmanager-cookies">
                      {t('cookieTypeTagManager')}
                    </label>
                    <p>{t('cookieTypeTagManagerDescription')}</p>
                  </div>
                </div>
                <div className="personalize_validation">
                  <button
                    type="button"
                    className="save-button"
                    onClick={handleSavePreferences}
                  >
                    {t('cookieConsentSave')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="button_wrapper">
                <button
                  type="button"
                  className="personalize-button"
                  onClick={handlePersonalize}
                >
                  {t('cookieConsentPersonalize')}
                </button>
                <button
                  type="button"
                  className="decline-button"
                  onClick={handleDeclineAll}
                >
                  {t('cookieConsentDecline')}
                </button>
                <button
                  type="button"
                  className="accept-button"
                  onClick={handleAcceptAll}
                >
                  {t('cookieConsentAccept')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {!isVisible && consent && (
        <button
          type="button"
          className="withdraw-consent-button"
          onClick={handleWithdrawConsent}
        >
          {t('cookieConsentWithdraw')}
        </button>
      )}
    </>
  );
};

export default CustomCookieConsent;
